
import React, { useState } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Invoice } from '@/services/billing/billingService';
import { format, isSameDay } from 'date-fns';

interface BillingCalendarProps {
  invoices: Invoice[];
  onDateClick?: (date: Date, invoices: Invoice[]) => void;
}

const BillingCalendar: React.FC<BillingCalendarProps> = ({ invoices, onDateClick }) => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  const getInvoicesForDate = (date: Date): Invoice[] => {
    return invoices.filter(invoice => 
      invoice.scheduled_date && isSameDay(new Date(invoice.scheduled_date), date)
    );
  };

  const getStatusColor = (status: Invoice['status']) => {
    switch (status) {
      case 'paid': return 'bg-green-500';
      case 'overdue': return 'bg-red-500';
      case 'unpaid': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      const dayInvoices = getInvoicesForDate(date);
      onDateClick?.(date, dayInvoices);
    }
  };

  const modifiers = {
    hasInvoices: (date: Date) => getInvoicesForDate(date).length > 0
  };

  const modifiersStyles = {
    hasInvoices: {
      backgroundColor: '#e3f2fd',
      fontWeight: 'bold'
    }
  };

  const selectedDateInvoices = selectedDate ? getInvoicesForDate(selectedDate) : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Invoice Calendar</CardTitle>
        </CardHeader>
        <CardContent>
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            className="rounded-md border"
          />
        </CardContent>
      </Card>

      {selectedDate && (
        <Card>
          <CardHeader>
            <CardTitle>
              Invoices for {format(selectedDate, 'MMMM d, yyyy')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedDateInvoices.length === 0 ? (
              <p className="text-gray-500">No invoices scheduled for this date</p>
            ) : (
              <div className="space-y-3">
                {selectedDateInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="font-medium">${invoice.total.toFixed(2)}</p>
                      <p className="text-sm text-gray-600">
                        Due: {invoice.due_date ? format(new Date(invoice.due_date), 'MMM d') : 'Not set'}
                      </p>
                    </div>
                    <Badge className={getStatusColor(invoice.status)}>
                      {invoice.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BillingCalendar;
